class CartDrawerManager {
  constructor() {
    this._sectionId   = window.cartDrawerConfig?.sectionId;
    this._isRefreshing = false;
    this._refreshQueued = false;

    this._onCartAdded   = this._onCartAdded.bind(this);
    this._onCartUpdated = this._onCartUpdated.bind(this);
    this._onCartRemoved = this._onCartRemoved.bind(this);
    this._onContentClick = this._onContentClick.bind(this);
    this._onContentKeydown = this._onContentKeydown.bind(this);
    this._onContentInput = this._onContentInput.bind(this);

    this._bindEvents();
  }

  _bindEvents() {
    document.addEventListener('cart:added',   this._onCartAdded);
    document.addEventListener('cart:updated', this._onCartUpdated);
    document.addEventListener('cart:removed', this._onCartRemoved);
    document.addEventListener('click',        this._onContentClick);
    document.addEventListener('keydown',      this._onContentKeydown);
    document.addEventListener('input',        this._onContentInput);
  }

  _isSilentAction(action) {
    return action === 'accessory_sync';
  }

  async _onCartAdded(e) {
    if (!this._isSilentAction(e.detail?.action)) await this.refresh();
    if (!e.detail?.action || e.detail.action === 'open_drawer') {
      window.DrawerManager?.open('cart');
    }
  }

  async _onCartUpdated(e) {
    if (!this._isSilentAction(e.detail?.action)) await this.refresh();
  }

  async _onCartRemoved(e) {
    if (!this._isSilentAction(e.detail?.action)) await this.refresh();
  }

  _onContentClick(e) {
    const summaryToggle = e.target.closest('[data-cart-summary-toggle]');
    if (summaryToggle) {
      const contentId = summaryToggle.getAttribute('aria-controls');
      const content = contentId ? document.getElementById(contentId) : null;
      if (!content) return;

      const isExpanded = summaryToggle.getAttribute('aria-expanded') === 'true';
      summaryToggle.setAttribute('aria-expanded', isExpanded ? 'false' : 'true');
      summaryToggle.setAttribute('aria-label', isExpanded ? 'Exibir resumo do carrinho' : 'Recolher resumo do carrinho');
      content.hidden = isExpanded;

      const header = summaryToggle.closest('[data-cart-summary-toggle-header]');
      if (header) header.classList.toggle('is-collapsed', isExpanded);

      const icon = summaryToggle.querySelector('[data-cart-summary-toggle-icon]');
      if (icon) icon.classList.toggle('rotate-180', isExpanded);
      return;
    }

    const removeBtn = e.target.closest('[data-remove-item]');
    if (removeBtn) {
      const key = removeBtn.dataset.lineItemKey;
      if (key) window.CartManager?.remove(key, { button: removeBtn });
      return;
    }

    const minusBtn = e.target.closest('[data-quantity-minus]');
    if (minusBtn) {
      const key = minusBtn.dataset.lineItemKey;
      const qty = this._getQuantity(minusBtn);
      if (!key) return;
      if (qty <= 1) {
        window.CartManager?.remove(key, { button: minusBtn });
      } else {
        window.CartManager?.update(key, qty - 1, { button: minusBtn });
      }
      return;
    }

    const plusBtn = e.target.closest('[data-quantity-plus]');
    if (plusBtn) {
      const key = plusBtn.dataset.lineItemKey;
      const qty = this._getQuantity(plusBtn);
      if (key) window.CartManager?.update(key, qty + 1, { button: plusBtn });
      return;
    }

    const quickAddBtn = e.target.closest('[data-cart-quick-add]');
    if (quickAddBtn) {
      const variantId = quickAddBtn.dataset.variantId;
      if (variantId) window.CartManager?.add(variantId, { button: quickAddBtn });
      return;
    }

    const cepBtn = e.target.closest('[data-cep-calculate]');
    if (cepBtn) {
      e.preventDefault();
      this._calculateShipping(cepBtn);
      return;
    }

    const shippingToggle = e.target.closest('[data-shipping-toggle]');
    if (shippingToggle) {
      e.preventDefault();
      this._toggleShippingRates(shippingToggle);
      return;
    }

    const couponBtn = e.target.closest('[data-coupon-apply]');
    if (couponBtn) {
      e.preventDefault();
      this._applyCoupon(couponBtn);
      return;
    }

    const couponRemoveBtn = e.target.closest('[data-coupon-remove]');
    if (couponRemoveBtn) {
      e.preventDefault();
      this._removeCoupon(couponRemoveBtn);
      return;
    }
  }

  _onContentKeydown(e) {
    if (e.key !== 'Enter') return;

    if (e.target.matches?.('[data-cep-input]')) {
      e.preventDefault();
      const btn = e.target.closest('[data-cep-form]')?.querySelector('[data-cep-calculate]');
      if (btn) this._calculateShipping(btn);
      return;
    }

    if (e.target.matches?.('[data-coupon-input]')) {
      e.preventDefault();
      const btn = e.target.closest('[data-coupon-form]')?.querySelector('[data-coupon-apply]');
      if (btn) this._applyCoupon(btn);
    }
  }

  _onContentInput(e) {
    if (e.target.matches?.('[data-coupon-input]')) {
      this._hideCouponResult(e.target.closest('[data-coupon-form]')?.querySelector('[data-coupon-result]'));
      return;
    }

    if (!e.target.matches?.('[data-cep-input]')) return;
    let value = e.target.value.replace(/\D/g, '').slice(0, 8);
    if (value.length > 5) value = `${value.slice(0, 5)}-${value.slice(5)}`;
    e.target.value = value;

    const btn = e.target.closest('[data-cep-form]')?.querySelector('[data-cep-calculate]');
    if (btn) this._setCepCalculated(btn, false);
  }

  _getQuantity(btn) {
    const item = btn.closest('[data-cart-item]');
    const valueEl = item?.querySelector('[data-quantity-value]');
    return parseInt(valueEl?.textContent?.trim() || '1', 10);
  }

  async _calculateShipping(button) {
    const wrapper = button.closest('[data-cep-form]');
    const input = wrapper?.querySelector('[data-cep-input]');
    const results = wrapper?.querySelector('[data-shipping-results]');
    if (!input || !results) return;

    const cep = input.value.replace(/\D/g, '');
    if (cep.length !== 8) {
      this._setCepCalculated(button, false);
      this._renderShippingMessage(results, 'Digite um CEP válido com 8 dígitos.', true);
      return;
    }

    button.disabled = true;
    this._renderShippingMessage(results, 'Calculando...', false);

    try {
      const addressRes = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const address = await addressRes.json();
      if (address.erro) throw new Error('CEP não encontrado');

      const params = new URLSearchParams({
        'shipping_address[zip]': cep,
        'shipping_address[country]': 'BR',
        'shipping_address[province]': address.uf || '',
        'shipping_address[city]': address.localidade || '',
      });

      const ratesRes = await fetch(`/cart/shipping_rates.json?${params.toString()}`);
      if (!ratesRes.ok) throw new Error(`HTTP ${ratesRes.status}`);

      const data = await ratesRes.json();
      this._renderShippingRates(results, data.shipping_rates || []);
      this._setCepCalculated(button, true);
    } catch (err) {
      console.error('[CartDrawerManager] Shipping calculation failed:', err);
      this._renderShippingMessage(results, 'CEP não encontrado. Verifique e tente novamente.', true);
      this._setCepCalculated(button, false);
    } finally {
      button.disabled = false;
    }
  }

  /** Alterna entre o ícone de seta (não calculado) e o de check (calculado). */
  _setCepCalculated(button, calculated) {
    const arrow = button.querySelector('[data-cep-icon-arrow]');
    const check = button.querySelector('[data-cep-icon-check]');
    if (arrow) arrow.hidden = calculated;
    if (check) check.hidden = !calculated;
  }

  _toggleShippingRates(button) {
    const group = button.closest('[data-shipping-rate-group]');
    const list = group?.querySelector('[data-shipping-rate-list]');
    if (!list) return;

    const isExpanded = button.getAttribute('aria-expanded') === 'true';
    button.setAttribute('aria-expanded', isExpanded ? 'false' : 'true');
    list.hidden = isExpanded;

    const icon = button.querySelector('[data-shipping-toggle-icon]');
    if (icon) icon.dataset.expanded = isExpanded ? 'false' : 'true';
  }

  _formatDeliveryRange(value) {
    const rawRange = String(value || '').trim();
    if (!rawRange) return '';

    const dateParts = rawRange.split(',').map(part => part.trim()).filter(Boolean);
    const hasOnlyDates = dateParts.length > 0 && dateParts.every(part => /^\d{4}-\d{2}-\d{2}$/.test(part));

    if (hasOnlyDates) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const days = dateParts.map(part => {
        const deliveryDate = new Date(`${part}T00:00:00`);
        return Math.max(1, Math.ceil((deliveryDate - today) / 86400000));
      });
      const firstDay = Math.min(...days);
      const lastDay = Math.max(...days);
      return firstDay === lastDay
        ? `em até ${firstDay} dias`
        : `de ${firstDay} a ${lastDay} dias`;
    }

    const numericRange = rawRange.match(/(\d+)\s*(?:-|a|até)\s*(\d+)/i);
    if (numericRange) return `de ${numericRange[1]} a ${numericRange[2]} dias`;

    return rawRange;
  }

  _escapeHtml(value) {
    return String(value || '').replace(/[&<>"']/g, character => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;',
    }[character]));
  }

  _renderShippingMessage(el, message, isError) {
    el.classList.remove('hidden');
    el.classList.add('flex');
    el.innerHTML = `<p class="m-[0px] py-[8px] text-[12px] ${isError ? 'text-[#c0392b]' : 'text-[#1a1a1a] opacity-[0.72]'}">${message}</p>`;
  }

  _renderShippingRates(el, rates) {
    if (!rates.length) {
      this._renderShippingMessage(el, 'Nenhuma opção de frete disponível para este CEP.', true);
      return;
    }

    el.classList.remove('hidden');
    el.classList.add('flex');
    const rateMarkup = rates
      .map(rate => {
        const priceNumber = parseFloat(String(rate.price || '0').replace(',', '.'));
        const priceStr = priceNumber === 0
          ? 'Grátis'
          : priceNumber.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
        const deliveryStr = this._formatDeliveryRange(rate.delivery_range || rate.delivery_date);

        return `
          <div class="cart-drawer-shipping-rate">
            <span class="cart-drawer-shipping-rate__name">${this._escapeHtml(rate.name || 'Frete')}</span>
            <span class="cart-drawer-shipping-rate__delivery">${this._escapeHtml(deliveryStr)}</span>
            <span class="cart-drawer-shipping-rate__price">${this._escapeHtml(priceStr)}</span>
          </div>`;
      })
      .join('');

    el.innerHTML = `
      <div data-shipping-rate-group>
        <button type="button" class="cart-drawer-shipping-toggle" aria-expanded="true" data-shipping-toggle>
          <span>Fretes</span>
          <svg data-shipping-toggle-icon data-expanded="true" xmlns="http://www.w3.org/2000/svg" width="10" height="6" viewBox="0 0 10 6" fill="none" aria-hidden="true">
            <path d="M1 1L5 5L9 1" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
        <div data-shipping-rate-list>${rateMarkup}</div>
      </div>`;
  }

  async _applyCoupon(button) {
    const wrapper = button.closest('[data-coupon-form]');
    const input = wrapper?.querySelector('[data-coupon-input]');
    const result = wrapper?.querySelector('[data-coupon-result]');
    if (!input) return;

    const code = input.value.trim();
    if (!code) return;

    button.disabled = true;
    this._hideCouponResult(result);

    try {
      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ discount: code }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const cart = await res.json();
      const applied = (cart.cart_level_discount_applications || []).some(
        app => app.title?.toLowerCase() === code.toLowerCase()
      );

      if (!applied) {
        this._showCouponResult(result, 'Cupom inválido ou não aplicável a este carrinho.');
        return;
      }

      await this.refresh();
    } catch (err) {
      console.error('[CartDrawerManager] Coupon apply failed:', err);
      this._showCouponResult(result, 'Não foi possível aplicar o cupom.');
    } finally {
      button.disabled = false;
    }
  }

  async _removeCoupon(button) {
    button.disabled = true;
    try {
      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ discount: '' }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await this.refresh();
    } catch (err) {
      console.error('[CartDrawerManager] Coupon remove failed:', err);
    } finally {
      button.disabled = false;
    }
  }

  _showCouponResult(el, message) {
    if (!el) return;
    el.textContent = message;
    el.classList.remove('hidden');
    el.classList.add('block');
    el.hidden = false;
  }

  _hideCouponResult(el) {
    if (!el) return;
    el.textContent = '';
    el.classList.remove('block');
    el.classList.add('hidden');
    el.hidden = true;
  }

  async refresh() {
    if (!this._sectionId) return;
    if (this._isRefreshing) {
      this._refreshQueued = true;
      return;
    }
    this._isRefreshing = true;
    this._setLoading(true);

    try {
      const res = await fetch(`/?sections=${this._sectionId}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const sections = await res.json();
      const html     = sections[this._sectionId];
      if (!html) return;

      const doc        = new DOMParser().parseFromString(html, 'text/html');
      const newContent = doc.querySelector('[data-cart-drawer-content]');
      const curContent = document.querySelector('[data-cart-drawer-content]');

      if (newContent && curContent) {
        curContent.innerHTML = newContent.innerHTML;
      }

      this._updateCount(doc);
    } catch (err) {
      console.error('[CartDrawerManager] Refresh failed:', err);
    } finally {
      this._isRefreshing = false;
      this._setLoading(false);
      if (this._refreshQueued) {
        this._refreshQueued = false;
        this.refresh();
      }
    }
  }

  _setLoading(show) {
    const el = document.querySelector('[data-cart-drawer-loading]');
    if (!el) return;
    show ? el.removeAttribute('hidden') : el.setAttribute('hidden', '');
  }

  _updateCount(doc) {
    const countEl = doc.querySelector('[data-cart-count]');
    if (!countEl) return;
    const count = countEl.textContent.trim();

    document.querySelectorAll('[data-cart-count]').forEach(el => {
      el.textContent = count;
    });
    document.querySelectorAll('[data-cart-count-zero]').forEach(el => {
      el.hidden = count !== '0';
    });
  }

  destroy() {
    document.removeEventListener('cart:added',   this._onCartAdded);
    document.removeEventListener('cart:updated', this._onCartUpdated);
    document.removeEventListener('cart:removed', this._onCartRemoved);
    document.removeEventListener('click',        this._onContentClick);
    document.removeEventListener('keydown',      this._onContentKeydown);
    document.removeEventListener('input',        this._onContentInput);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  window.CartDrawerManager = new CartDrawerManager();
});
